// Operator authentication — two-step login with TOTP enrollment.
//
// Step 1 (POST /auth/login): email + password ONLY. Server branches:
//   - enrolled   → 'totp_required'  → advance to the code-only TOTP step.
//   - unenrolled → 'enroll_required'→ show the QR + secret + confirm-code field.
// Step 2 (POST /auth/login/totp)  → session (enrolled path).
// Step 3 (POST /auth/login/enroll)→ session (activates TOTP on first code).
//
// The short-lived `pending` token is the ONLY step-1↔step-2/3 linkage; it lives
// in memory (never localStorage). A full session ({token,user,expiresAt}) is
// issued ONLY by step 2 / step 3.

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import {
  ApiError,
  api as defaultApi,
  getStoredToken,
  setStoredToken,
  clearStoredToken,
  type ApiClient,
} from './api';
import type {
  OperatorUser,
  OperatorLoginRequest,
  OperatorLoginResponse,
  OperatorLoginStep1Response,
  OperatorSessionResponse,
} from './types';

/** Which panel the login flow is showing. Mirrors the server-side stage. */
export type LoginStep = 'restoring' | 'credentials' | 'totp' | 'enroll';

/** Enrollment payload carried between step 1 and the enroll-confirm step (in memory only). */
interface Enrollment {
  secret: string;
  otpauthUri: string;
}

interface AuthState {
  user: OperatorUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  loginStep: LoginStep;
  /** Present only during the enroll step. */
  enrollment: Enrollment | null;
}

interface AuthContextValue extends AuthState {
  /** Step 1 — email + password. Advances to 'totp' | 'enroll' (or stays on error). */
  login: (credentials: OperatorLoginRequest) => Promise<void>;
  /** Step 2 — submit the 6-digit code for an already-enrolled operator. */
  submitTotp: (code: string) => Promise<void>;
  /** Step 3 — submit the first code to activate TOTP for an unenrolled operator. */
  submitEnroll: (code: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const ANON: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  loginStep: 'credentials',
  enrollment: null,
};

function messageOf(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return fallback;
}

export function AuthProvider({
  children,
  client = defaultApi,
}: {
  children: ReactNode;
  client?: ApiClient;
}) {
  const [state, setState] = useState<AuthState>({
    ...ANON,
    isLoading: true,
    loginStep: 'restoring',
  });
  // The pending token is short-lived and in-memory only (never persisted).
  const [pending, setPending] = useState<string | null>(null);

  const checkSession = useCallback(async () => {
    const token = getStoredToken();
    if (!token) {
      setState(ANON);
      return;
    }
    try {
      const data = await client.get<OperatorSessionResponse>('/auth/me');
      setState({
        user: data.user,
        isAuthenticated: true,
        isLoading: false,
        error: null,
        loginStep: 'credentials',
        enrollment: null,
      });
    } catch {
      clearStoredToken();
      setState(ANON);
    }
  }, [client]);

  useEffect(() => {
    void checkSession();
  }, [checkSession]);

  /** Common success path for step 2 / step 3 — store token, set user, clear pending. */
  const applySession = useCallback((data: OperatorLoginResponse) => {
    setStoredToken(data.token);
    setPending(null);
    setState({
      user: data.user,
      isAuthenticated: true,
      isLoading: false,
      error: null,
      loginStep: 'credentials',
      enrollment: null,
    });
  }, []);

  const login = useCallback(
    async (credentials: OperatorLoginRequest) => {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));
      try {
        const data = await client.post<OperatorLoginStep1Response>('/auth/login', {
          email: credentials.email,
          password: credentials.password,
        });
        if (data.status === 'ok') {
          // Defensive direct-session branch (server may never emit it).
          applySession(data);
          return;
        }
        if (data.status === 'totp_required') {
          setPending(data.pending);
          setState({ ...ANON, loginStep: 'totp' });
          return;
        }
        // enroll_required
        setPending(data.pending);
        setState({
          ...ANON,
          loginStep: 'enroll',
          enrollment: { secret: data.secret, otpauthUri: data.otpauthUri },
        });
      } catch (err) {
        clearStoredToken();
        setPending(null);
        setState({ ...ANON, error: messageOf(err, 'Login failed') });
      }
    },
    [client, applySession],
  );

  const submitTotp = useCallback(
    async (code: string) => {
      if (!pending) {
        setState({ ...ANON, error: 'Login session expired, start again' });
        return;
      }
      setState((prev) => ({ ...prev, isLoading: true, error: null }));
      try {
        const data = await client.post<OperatorLoginResponse>('/auth/login/totp', {
          pending,
          code,
        });
        applySession(data);
      } catch (err) {
        const code2 = err instanceof ApiError ? err.code : '';
        if (code2 === 'pending_expired') {
          // Pending token dead — restart from the credentials step.
          setPending(null);
          setState({ ...ANON, error: messageOf(err, 'Login session expired, start again') });
          return;
        }
        // Bad/replayed code — stay on the totp step with an error.
        setState((prev) => ({
          ...prev,
          isLoading: false,
          loginStep: 'totp',
          error: messageOf(err, 'Invalid authentication code'),
        }));
      }
    },
    [client, pending, applySession],
  );

  const submitEnroll = useCallback(
    async (code: string) => {
      if (!pending) {
        setState({ ...ANON, error: 'Login session expired, start again' });
        return;
      }
      setState((prev) => ({ ...prev, isLoading: true, error: null }));
      try {
        const data = await client.post<OperatorLoginResponse>('/auth/login/enroll', {
          pending,
          code,
        });
        applySession(data);
      } catch (err) {
        const code2 = err instanceof ApiError ? err.code : '';
        if (code2 === 'pending_expired') {
          setPending(null);
          setState({ ...ANON, error: messageOf(err, 'Login session expired, start again') });
          return;
        }
        // Bad first code — keep the QR + secret (same pending) and show the error.
        setState((prev) => ({
          ...prev,
          isLoading: false,
          loginStep: 'enroll',
          error: messageOf(err, 'Invalid authentication code'),
        }));
      }
    },
    [client, pending, applySession],
  );

  const logout = useCallback(async () => {
    try {
      await client.post('/auth/logout');
    } catch {
      /* non-blocking */
    }
    clearStoredToken();
    setPending(null);
    setState(ANON);
  }, [client]);

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, login, submitTotp, submitEnroll, logout }),
    [state, login, submitTotp, submitEnroll, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
